import { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import Plot from 'react-plotly.js';
import { selectRTFO } from '../../reducers/rtfoSlice';
import { selectUser } from '../../reducers/userSlice';
import { chartColors } from '../../helpers/chartColors';
import { useTheme } from '@mui/styles';
import { CircularProgress, Typography } from '@mui/material';
import { rtfoConversionFactors } from './rtfoConversionFactors';

export const RTFOPumpdownChart = ({ 
  gridView, 
  editMode, 
  pumpdownData, 
  pumpdownFalloffTimestamps, 
  setPumpdownFalloffTimestamps, 
  postPerfFalloffTimestamps, 
  setPostPerfFalloffTimestamps, 
  isLoadingPumpdownData,
  pressureTestTimestamps,
  setPressureTestTimestamps,
  postPlugFalloffTimestamps,
  setPostPlugFalloffTimestamps,
  customTimestamps,
  setCustomTimestamps,
  initialRegression,
  initialRegressionStartTimestamp,
  pumpdownRegression,
  pumpdownRegressionStartTimestamp,
  setPumpdownRegressionStartTimestamp,
  postPerfRegression,
  postPerfRegressionStartTimestamp,
  setPostPerfRegressionStartTimestamp
}) => {

  const theme = useTheme();
  const user = useSelector(selectUser);
  const rtfo = useSelector(selectRTFO);
  const currentRTFO = rtfo?.value;
  const currentStage = currentRTFO?.stageData?.[currentRTFO?.currentStageName];
  const units = currentRTFO?.settings?.units || 'metric';


  const darkMode = user?.value?.darkMode;
  const maxDepth = Math.max(...(pumpdownData || []).map(d => (d.depth || 0) * (units === 'field' ? rtfoConversionFactors.depth : 1)));

  const data = useMemo(() => {
    if (currentStage && pumpdownData) {
      return getChartData();
    };

    function getChartData() {
      try {
        // add traces
        let traces = [];

        const X = [];
        const YRate = [];
        const YPressure = [];
        const YDepth = [];
        const YVoltage = [];
        const YTension = [];
        
        pumpdownData?.forEach(d => {
          X.push(d.timestamp);
          YRate.push((d.rate || null) * (units === 'field' ? rtfoConversionFactors.rate : 1));
          YPressure.push((d.pressure || (d.leftPumpPressure + d.rightPumpPressure) / 2 || null) * (units === 'field' ? rtfoConversionFactors.pressure : 1));
          YDepth.push(d.depth > 0 ? (d.depth * (units === 'field' ? rtfoConversionFactors.depth : 1)) : null);
          YVoltage.push(d.voltage || null);
          YTension.push(d.tension / 1000 || null);
        });

        // Plot initial fit
        if (!isNaN(initialRegression?.A) && !isNaN(initialRegression?.B)) {
          const XInitialFit = [];
          const YInitialFit = [];
  
          pumpdownData.forEach((d,i) => {
            if (d.timestamp > initialRegressionStartTimestamp) {
              XInitialFit.push(d.timestamp)
              YInitialFit.push((initialRegression?.A * Math.pow(((d.timestamp - initialRegressionStartTimestamp) / 60000), initialRegression?.B)) * (units === 'field' ? rtfoConversionFactors.pressure : 1));
            };
          });
  
          traces.push(
            { // initial fit
              x: XInitialFit,
              y: YInitialFit,
              mode: 'line',
              type: 'scatter',
              xaxis: 'x1',
              yaxis: 'y1',
              name: 'Initial Fit',
              line: {
                width: 2,
                color: 'grey',
                opacity: 1,
                dash: 'dot'
              },
              hoverinfo: editMode && 'none'
            }
          );
        };

        // Plot pumpdown fit
        if (!isNaN(pumpdownRegression?.A) && !isNaN(pumpdownRegression?.B)) {
          const XPumpdownISIPFit = [];
          const YPumpdownISIPFit = [];
          
          pumpdownData.forEach((d,i) => {
            if (d.timestamp > pumpdownRegressionStartTimestamp) {
              XPumpdownISIPFit.push(d.timestamp)
              YPumpdownISIPFit.push((pumpdownRegression?.A * Math.pow(((d.timestamp - pumpdownRegressionStartTimestamp) / 60000), pumpdownRegression?.B)) * (units === 'field' ? rtfoConversionFactors.pressure : 1));
            };
          });
          
          traces.push(
            { // pumpdown isip fit
              x: XPumpdownISIPFit,
              y: YPumpdownISIPFit,
              mode: 'line',
              type: 'scatter',
              xaxis: 'x1',
              yaxis: 'y1',
              name: 'Pumpdown ISIP Fit',
              line: {
                width: 2,
                color: chartColors[2],
                opacity: 1,
                dash: 'dot'
              },
              hoverinfo: editMode && 'none'
            }
          );
        };

        // Plot post perf fit
        if (!isNaN(postPerfRegression?.A) && !isNaN(postPerfRegression?.B)) {
          const XPostPerfISIPFit = [];
          const YPostPerfISIPFit = [];
          
          pumpdownData.forEach((d,i) => {
            if (d.timestamp > postPerfRegressionStartTimestamp) {
              XPostPerfISIPFit.push(d.timestamp)
              YPostPerfISIPFit.push((postPerfRegression?.A * Math.pow(((d.timestamp - postPerfRegressionStartTimestamp) / 60000), postPerfRegression?.B)) * (units === 'field' ? rtfoConversionFactors.pressure : 1));
            };
          });
          
          traces.push(
            { // pumpdown isip fit
              x: XPostPerfISIPFit,
              y: YPostPerfISIPFit,
              mode: 'line',
              type: 'scatter',
              xaxis: 'x1',
              yaxis: 'y1',
              name: 'Post Perf ISIP Fit',
              line: {
                width: 2,
                color: chartColors[6],
                opacity: 1,
                dash: 'dot'
              },
              hoverinfo: editMode && 'none'
            }
          );
        };


        traces.push(
          { // pump pressure
            x: X,
            y: YPressure,
            mode: 'line',
            type: 'scattergl',
            xaxis: 'x1',
            yaxis: 'y1',
            name: `Avg. Pump Pressure (${units === 'field' ? 'psi' : 'MPa'})`,
            line: {
              width: 2,
              color: chartColors[2],
              opacity: 1
            },
            hoverinfo: editMode && 'none'
          }
        );
        traces.push(
          { // rate
            x: X,
            y: YRate,
            mode: 'line',
            type: 'scattergl',
            xaxis: 'x1',
            yaxis: 'y2',
            name: `Pumpdown Rate (${units === 'field' ? 'bbl/min' : 'm3/min'})`,
            line: {
              width: 2,
              color: chartColors[3],
              opacity: 1
            },
            hoverinfo: editMode && 'none'
          }
        );
        traces.push(
          { // depth
            x: X,
            y: YDepth,
            mode: 'line',
            type: 'scattergl',
            xaxis: 'x1',
            yaxis: 'y3',
            name: `Depth (${units === 'field' ? 'ft' : 'm'})`,
            line: {
              width: 2,
              color: 'black',
              opacity: 1
            },
            hoverinfo: editMode && 'none'
          }
        );
        traces.push(
          { // voltage
            x: X,
            y: YVoltage,
            mode: 'line',
            type: 'scattergl',
            xaxis: 'x1',
            yaxis: 'y4',
            name: 'Voltage',
            line: {
              width: 2,
              color: chartColors[6],
              opacity: 1
            },
            hoverinfo: editMode && 'none'
          }
        );
        traces.push(
          { // tension
            x: X,
            y: YTension,
            mode: 'line',
            type: 'scattergl',
            xaxis: 'x1',
            yaxis: 'y4',
            name: 'Line Tension (lbf/1000)',
            line: {
              width: 2,
              color: chartColors[8],
              opacity: 1
            },
            hoverinfo: editMode && 'none'
          }
        );

  
        return traces;
  
      } catch (error) {
        console.log(error);
        return [];
      };
    };
  }, [currentStage, pumpdownData, editMode, pumpdownRegression?.A, pumpdownRegression?.B, postPerfRegression?.A, postPerfRegression?.B, units, initialRegressionStartTimestamp, initialRegression?.A, initialRegression?.B, pumpdownRegressionStartTimestamp, postPerfRegressionStartTimestamp]);

  const xRangeRef = useRef(null);

  useEffect(() => {
    xRangeRef.current = null;
  }, [pumpdownData]);

  // Function to get current range after user interaction or initial set
  const getCurrentRange = (plotlyEvent) => {
    if (plotlyEvent && plotlyEvent['xaxis.range[0]'] && plotlyEvent['xaxis.range[1]']) {
      xRangeRef.current = [plotlyEvent['xaxis.range[0]'], plotlyEvent['xaxis.range[1]']];
    };
  };

  const onRelayout = (event) => {
    getCurrentRange(event);
  };

  const layout = useMemo(() => {
    return {
      yaxis: { // pump pressure y axis
        constraintoward: 'domain',
        linecolor: chartColors[2],
        gridcolor: darkMode ? 'grey' : 'lightgrey',
        color: chartColors[2],
        title: {
          text: `Pumpdown Pressure (${units === 'field' ? 'psi' : 'MPa'})`,
          font: {
            size: 12
          },
        },
        anchor: 'x',
        range: units === 'field' ? [0, 15000] : [0, 100],
        tick0: 0,
        dtick: units === 'field' ? 3000 : 20,
        side: 'left',
        hoverformat: '.2f'
      },
      yaxis2: { // rate y axis
        constraintoward: 'domain',
        linecolor: chartColors[3],
        showgrid: false,
        color: chartColors[3],
        title: {
          text: `Pumpdown Rate (${units === 'field' ? 'bbl/min' : 'm3/min'})`,
          font: {
            size: 12
          },
        },
        range: units === 'field' ? [0, 250] : [0, 10],
        anchor: 'free',
        overlaying: 'y',
        side: 'left',
        position: 0,
        hoverformat: '.2f'
      },
      yaxis3: { // depth y axis
        constraintoward: 'domain',
        linecolor: 'black',
        showgrid: false,
        color: 'black',
        title: {
          text: `Depth (${units === 'field' ? 'ft' : 'm'})`,
          font: {
            size: 12
          },
        },
        rangemode: 'nonnegative',
        range: units === 'field' ? [maxDepth - 600, maxDepth + 300] : [maxDepth - 200, maxDepth + 100],
        overlaying: 'y',
        side: 'right',
        anchor: 'x',
        hoverformat: '.2f'
      },
      yaxis4: { // voltage y axis
        constraintoward: 'domain',
        linecolor: chartColors[6],
        showgrid: false,
        color: chartColors[6],
        title: {
          text: 'Voltage (V) and Line Tension (lbf/1000)',
          font: {
            size: 12
          },
        },
        range: units === 'field' ? [-1500, 1500] : [-5, 5],
        overlaying: 'y',
        side: 'right',
        anchor: 'free',
        position: 1,
        uirevision: 'true',
        hoverformat: '.2f'
      },
      xaxis: { // Date x axis
        type: 'date',
        title: {
          text: 'Date',
          font: {
            size: 12
          }
        },
        linecolor: darkMode ? 'lightgrey' : 'black',
        gridcolor: darkMode ? 'grey' : 'lightgrey',
        color: darkMode ? 'lightgrey' : 'black',
        anchor: 'y',
        domain: [(gridView ? 0.1 : 0.05), (gridView ? 0.9 : 0.95)],
        range: xRangeRef.current || [undefined, undefined],
        showspikes: !editMode ? true : false,
        spikethickness: 2,
        spikedash: "dot",
        spikecolor: "#999999",
        spikemode: "across",
      },
      hovermode: "x",
      hoverdistance: 10, // Distance to show hover label of data point
      spikedistance: 1000,
      autosize: true,
      showlegend: true,
      legend: {
        orientation: "h",
        yanchor: "top",
        font: {
          color: darkMode ? 'lightgrey' : 'grey',
          size: 10
        },
        y: (gridView ? -0.15 : -0.1),
      },
      shapes: [
        pumpdownFalloffTimestamps?.startTimestamp && pumpdownFalloffTimestamps?.endTimestamp && {
          type: 'rect',
          xref: 'x',
          yref: 'paper',
          x0: new Date(pumpdownFalloffTimestamps?.startTimestamp),
          y0: 0,
          x1: new Date(pumpdownFalloffTimestamps?.endTimestamp),
          y1: 1,
          line: {
            width: 0,
          },
          fillcolor: chartColors[0],
          opacity: 0.25
        },
        editMode === 'pumpdownStart' && pumpdownFalloffTimestamps?.startTimestamp && {
          type: 'line',
          xref: 'x',
          yref: 'paper',
          x0: new Date(pumpdownFalloffTimestamps?.startTimestamp),
          y0: 0,
          x1: new Date(pumpdownFalloffTimestamps?.startTimestamp),
          y1: 1,
          line: {
            width: 3,
            color: chartColors[0],
          },
          opacity: 1
        },
        editMode === 'pumpdownEnd' && pumpdownFalloffTimestamps?.endTimestamp && {
          type: 'line',
          xref: 'x',
          yref: 'paper',
          x0: new Date(pumpdownFalloffTimestamps?.endTimestamp),
          y0: 0,
          x1: new Date(pumpdownFalloffTimestamps?.endTimestamp),
          y1: 1,
          line: {
            width: 3,
            color: chartColors[0],
          },
          opacity: 1
        },
        postPerfFalloffTimestamps?.startTimestamp && postPerfFalloffTimestamps?.endTimestamp && {
          type: 'rect',
          xref: 'x',
          yref: 'paper',
          x0: new Date(postPerfFalloffTimestamps?.startTimestamp),
          y0: 0,
          x1: new Date(postPerfFalloffTimestamps?.endTimestamp),
          y1: 1,
          line: {
            width: 0,
          },
          fillcolor: chartColors[6],
          opacity: 0.25
        },
        editMode === 'postPerfStart' && postPerfFalloffTimestamps?.startTimestamp && {
          type: 'line',
          xref: 'x',
          yref: 'paper',
          x0: new Date(postPerfFalloffTimestamps?.startTimestamp),
          y0: 0,
          x1: new Date(postPerfFalloffTimestamps?.startTimestamp),
          y1: 1,
          line: {
            width: 3,
            color: chartColors[6],
          },
          opacity: 1
        },
        editMode === 'postPerfEnd' && postPerfFalloffTimestamps?.endTimestamp && {
          type: 'line',
          xref: 'x',
          yref: 'paper',
          x0: new Date(postPerfFalloffTimestamps?.endTimestamp),
          y0: 0,
          x1: new Date(postPerfFalloffTimestamps?.endTimestamp),
          y1: 1,
          line: {
            width: 3,
            color: chartColors[6],
          },
          opacity: 1
        },
        pressureTestTimestamps?.startTimestamp > 0 && pressureTestTimestamps?.endTimestamp && {
          type: 'rect',
          xref: 'x',
          yref: 'paper',
          x0: new Date(pressureTestTimestamps?.startTimestamp),
          y0: 0,
          x1: new Date(pressureTestTimestamps?.endTimestamp),
          y1: 1,
          line: {
            width: 0,
          },
          fillcolor: chartColors[8],
          opacity: 0.25
        },
        editMode === 'pressureTestStart' && pressureTestTimestamps?.startTimestamp && {
          type: 'line',
          xref: 'x',
          yref: 'paper',
          x0: new Date(pressureTestTimestamps?.startTimestamp),
          y0: 0,
          x1: new Date(pressureTestTimestamps?.startTimestamp),
          y1: 1,
          line: {
            width: 3,
            color: chartColors[8],
          },
          opacity: 1
        },
        editMode === 'pressureTestEnd' && pressureTestTimestamps?.endTimestamp && {
          type: 'line',
          xref: 'x',
          yref: 'paper',
          x0: new Date(pressureTestTimestamps?.endTimestamp),
          y0: 0,
          x1: new Date(pressureTestTimestamps?.endTimestamp),
          y1: 1,
          line: {
            width: 3,
            color: chartColors[8],
          },
          opacity: 1
        },
        postPlugFalloffTimestamps?.startTimestamp > 0 && postPlugFalloffTimestamps?.endTimestamp && {
          type: 'rect',
          xref: 'x',
          yref: 'paper',
          x0: new Date(postPlugFalloffTimestamps?.startTimestamp),
          y0: 0,
          x1: new Date(postPlugFalloffTimestamps?.endTimestamp),
          y1: 1,
          line: {
            width: 0,
          },
          fillcolor: chartColors[1],
          opacity: 0.25
        },
        initialRegressionStartTimestamp && {
          type: 'line',
          xref: 'x',
          yref: 'paper',
          x0: new Date(initialRegressionStartTimestamp),
          y0: 0,
          x1: new Date(initialRegressionStartTimestamp),
          y1: 1,
          line: {
            width: 1,
            color: 'grey',
            dash: 'dot'
          },
          opacity: 1
        },
        pumpdownRegressionStartTimestamp && {
          type: 'line',
          xref: 'x',
          yref: 'paper',
          x0: new Date(pumpdownRegressionStartTimestamp),
          y0: 0,
          x1: new Date(pumpdownRegressionStartTimestamp),
          y1: 1,
          line: {
            width: 1,
            color: 'grey',
            dash: 'dot'
          },
          opacity: 1
        },
        postPerfRegressionStartTimestamp && {
          type: 'line',
          xref: 'x',
          yref: 'paper',
          x0: new Date(postPerfRegressionStartTimestamp),
          y0: 0,
          x1: new Date(postPerfRegressionStartTimestamp),
          y1: 1,
          line: {
            width: 1,
            color: 'grey',
            dash: 'dot'
          },
          opacity: 1
        },
        customTimestamps?.startTimestamp > 0 && customTimestamps?.endTimestamp && {
          type: 'rect',
          xref: 'x',
          yref: 'paper',
          x0: new Date(customTimestamps?.startTimestamp),
          y0: 0,
          x1: new Date(customTimestamps?.endTimestamp),
          y1: 1,
          line: {
            width: 0,
          },
          fillcolor: chartColors[5],
          opacity: 0.25
        },
        editMode === 'customStart' && customTimestamps?.startTimestamp && {
          type: 'line',
          xref: 'x',
          yref: 'paper',
          x0: new Date(customTimestamps?.startTimestamp),
          y0: 0,
          x1: new Date(customTimestamps?.startTimestamp),
          y1: 1,
          line: {
            width: 3,
            color: chartColors[5],
          },
          opacity: 1
        },
        editMode === 'customEnd' && customTimestamps?.endTimestamp && {
          type: 'line',
          xref: 'x',
          yref: 'paper',
          x0: new Date(customTimestamps?.endTimestamp),
          y0: 0,
          x1: new Date(customTimestamps?.endTimestamp),
          y1: 1,
          line: {
            width: 3,
            color: chartColors[5],
          },
          opacity: 1
        },
      ],
      annotations: [
        {
          text: "Pumpdown",
            font: {
            size: 14,
              color: darkMode ? 'lightgrey' : 'black'
          },
          showarrow: false,
          align: 'center',
          x: 0.5, //position in x domain
          y: (gridView ? 1.135 : 1.05), //position in y domain
          xref: 'paper',
          yref: 'paper',
        },
        {
          text: `Pad: ${currentRTFO?.name} | Well: ${currentStage?.wellName} | Stage: ${currentStage?.stageNumber}`,
            font: {
            size: 10,
              color: darkMode ? 'lightgrey' : 'black'
          },
          showarrow: false,
          align: 'center',
          x: 0.5, //position in x domain
          y: (gridView ? 1.08 : 1.02), //position in y domain
          xref: 'paper',
          yref: 'paper',
        },
        pumpdownFalloffTimestamps?.startTimestamp && pumpdownFalloffTimestamps?.endTimestamp && {
          x: new Date((pumpdownFalloffTimestamps?.startTimestamp + pumpdownFalloffTimestamps?.endTimestamp) / 2),
          y: 1,
          xref: 'x',
          yref: 'paper',
          text: 'Pumpdown',
          textangle: '-90',
          bgcolor: ('transparent'),
          font: {color: chartColors[0], weight: 'bold'},
          borderpad: 1,
          showarrow: false,
          ax: 0,
          ay: -40
        },
        postPerfFalloffTimestamps?.startTimestamp && postPerfFalloffTimestamps?.endTimestamp && {
          x: new Date((postPerfFalloffTimestamps?.startTimestamp + postPerfFalloffTimestamps?.endTimestamp) / 2),
          y: 1,
          xref: 'x',
          yref: 'paper',
          text: 'Post Perf',
          textangle: '-90',
          bgcolor: ('transparent'),
          font: {color: chartColors[6], weight: 'bold'},
          borderpad: 1,
          showarrow: false,
          ax: 0,
          ay: -40
        },
        pressureTestTimestamps?.startTimestamp > 0 && pressureTestTimestamps?.endTimestamp && {
          x: new Date((pressureTestTimestamps?.startTimestamp + pressureTestTimestamps?.endTimestamp) / 2),
          y: 1,
          xref: 'x',
          yref: 'paper',
          text: 'Press. Test',
          textangle: '-90',
          bgcolor: ('transparent'),
          font: {color: chartColors[8], weight: 'bold'},
          borderpad: 1,
          showarrow: false,
          ax: 0,
          ay: -40
        },
        postPlugFalloffTimestamps?.startTimestamp > 0 && postPlugFalloffTimestamps?.endTimestamp && {
          x: new Date((postPlugFalloffTimestamps?.startTimestamp + postPlugFalloffTimestamps?.endTimestamp) / 2),
          y: 1,
          xref: 'x',
          yref: 'paper',
          text: 'Post Plug',
          textangle: '-90',
          bgcolor: ('transparent'),
          font: {color: chartColors[1], weight: 'bold'},
          borderpad: 1,
          showarrow: false,
          ax: 0,
          ay: -40
        },
        customTimestamps?.startTimestamp > 0 && customTimestamps?.endTimestamp && {
          x: new Date((customTimestamps?.startTimestamp + customTimestamps?.endTimestamp) / 2),
          y: 1,
          xref: 'x',
          yref: 'paper',
          text: 'Custom',
          textangle: '-90',
          bgcolor: ('transparent'),
          font: {color: chartColors[5], weight: 'bold'},
          borderpad: 1,
          showarrow: false,
          ax: 0,
          ay: -40
        }
      ],
      uirevision: 'true',
      margin: {"t": 60, "l": 60, "r": 60, "b": 50},
      images: [
        {
          x: (gridView ? 0.06 : 0.035),
          y: (gridView ? 1.05 : 1.025),
          sizex: (gridView ? 0.1 : 0.05),
          sizey: 0.1,
          source: `/img/ssd_logomark_chart_${darkMode ? 'white' : 'primary'}.png`,
          xref: "page",
          yref: "page",
          xanchor: "right",
          yanchor: "bottom",
          opacity: 0.75
        }
      ],
      plot_bgcolor: "transparent",
      paper_bgcolor: theme?.palette?.elevationOne?.[(darkMode ? 'dark' : 'light')]
    };
  }, [currentRTFO?.name, currentStage?.stageNumber, currentStage?.wellName, customTimestamps?.endTimestamp, customTimestamps?.startTimestamp, darkMode, editMode, gridView, initialRegressionStartTimestamp, maxDepth, postPerfFalloffTimestamps?.endTimestamp, postPerfFalloffTimestamps?.startTimestamp, postPerfRegressionStartTimestamp, postPlugFalloffTimestamps?.endTimestamp, postPlugFalloffTimestamps?.startTimestamp, pressureTestTimestamps?.endTimestamp, pressureTestTimestamps?.startTimestamp, pumpdownFalloffTimestamps?.endTimestamp, pumpdownFalloffTimestamps?.startTimestamp, pumpdownRegressionStartTimestamp, theme?.palette?.elevationOne, units]);

  const config = {
    displaylogo: false,
    toImageButtonOptions: {
      format: 'png', // one of png, svg, jpeg, webp
      filename: `${currentRTFO?.name}-pumpdown-${new Date().toISOString().split('T')[0]}`,
      height: 550,
      width: 960,
      scale: 1 // Multiply title/legend/axis/canvas sizes by this factor
    }
  };

  return (
    <div style={{height: '100%', width: '100%'}}>
      {isLoadingPumpdownData?.current ?
        <div style={{height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center'}}>
        <Typography style={{marginBottom: 20}}>Downloading pumpdown data...</Typography>
        <CircularProgress />
      </div> :
      <Plot
        data={data}
        layout={layout}
        onRelayout={onRelayout}
        // onZoom={onZoom}
        config={config}
        useResizeHandler={true}
        style={{width: '100%', height: '100%'}}
        onClick={handlePlotClick}
      />}

    </div>
  );

  // FUNCTIONS
  function handlePlotClick(e) {
    try {
      if (editMode === 'pumpdownStart') {
        const newStart = new Date(e.points?.[0]?.x)?.getTime();
        setPumpdownFalloffTimestamps(prev => ({...prev, startTimestamp: newStart}));
      } else if (editMode === 'pumpdownEnd') {
        const newEnd = new Date(e.points?.[0]?.x)?.getTime();
        setPumpdownFalloffTimestamps(prev => ({...prev, endTimestamp: newEnd}));
      } else if (editMode === 'postPerfStart') {
        const newStart = new Date(e.points?.[0]?.x)?.getTime();
        setPostPerfFalloffTimestamps(prev => ({...prev, startTimestamp: newStart}));
      } else if (editMode === 'postPerfEnd') {
        const newEnd = new Date(e.points?.[0]?.x)?.getTime();
        setPostPerfFalloffTimestamps(prev => ({...prev, endTimestamp: newEnd}));
      } else if (editMode === 'pressureTestStart') {
        const newStart = new Date(e.points?.[0]?.x)?.getTime();
        setPressureTestTimestamps(prev => ({...prev, startTimestamp: newStart}));
      } else if (editMode === 'pressureTestEnd') {
        const newEnd = new Date(e.points?.[0]?.x)?.getTime();
        setPressureTestTimestamps(prev => ({...prev, endTimestamp: newEnd}));

        // update post plug interval
        const newPostPlugStart = newEnd;
        const newPostPlugEnd = postPlugFalloffTimestamps?.endTimestamp || pumpdownData?.findLast(d => d.timestamp > newPostPlugStart && d.timestamp < postPerfFalloffTimestamps?.startTimestamp && d.rate < 0.5)?.timestamp;
        setPostPlugFalloffTimestamps({ startTimestamp: newPostPlugStart, endTimestamp: newPostPlugEnd });
      } else if (editMode === 'postPlugStart') {
        const newStart = new Date(e.points?.[0]?.x)?.getTime();
        setPostPlugFalloffTimestamps(prev => ({...prev, startTimestamp: newStart}));
      } else if (editMode === 'postPlugEnd') {
        const newEnd = new Date(e.points?.[0]?.x)?.getTime();
        setPostPlugFalloffTimestamps(prev => ({...prev, endTimestamp: newEnd}));
      } else if (editMode === 'customStart') {
        const newStart = new Date(e.points?.[0]?.x)?.getTime();
        setCustomTimestamps(prev => ({...prev, startTimestamp: newStart}));
      } else if (editMode === 'customEnd') {
        const newEnd = new Date(e.points?.[0]?.x)?.getTime();
        setCustomTimestamps(prev => ({...prev, endTimestamp: newEnd}));
      } else if (editMode === 'pumpdownRegression') {
        const newTimestamp = new Date(e.points?.[0]?.x)?.getTime();
        setPumpdownRegressionStartTimestamp(newTimestamp);
      } else if (editMode === 'postPerfRegression') {
        const newTimestamp = new Date(e.points?.[0]?.x)?.getTime();
        setPostPerfRegressionStartTimestamp(newTimestamp);
      };

    } catch (error) {
      console.log(error);
    };
  };

};
